import * as React from 'react';
import { styled, useTheme } from '@mui/material/styles';
import {
  List,
  ListItemButton,
  ListItemText,
  useMediaQuery,
  Tooltip,
  Box,
} from '@mui/material';
import Collapse from '@mui/material/Collapse';
import { Link, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { CustomListItem, CustomListItemButton } from './index';

const ListItemToggleIcon = styled('img')(({ theme }) => ({
  width: theme.spacing(4),
  transition: 'transform 0.2s ease-in-out',
  marginTop: '-6px',
  '&.rotated': {
    transform: 'rotate(180deg)',
  },
  // Responsive sizing between md and xl
  [theme.breakpoints.only('md')]: {
    width: '18px',
  },
  [theme.breakpoints.only('lg')]: {
    width: '20px',
  },
}));

// Separate toggle icon button that preserves hover effects
const ToggleIconButton = styled(Box)(({ theme }) => ({
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(5),
  zIndex: 2,
  position: 'absolute',
  right: 12,
  height: '100%',
  // Important - this makes the toggwle button transparent to hover events
  pointerEvents: 'all',
}));

// Utility function to determine if path is part of this subitem's subtree
const isPartOfSubtree = (path, item) => {
  if (!path || !item) return false;

  // Check if path matches this item directly
  if (item.link && (path === item.link || path.startsWith(item.link + '/'))) {
    return true;
  }

  // Check if path matches any of this item's children
  if (item.subItems && item.subItems.length > 0) {
    return item.subItems.some(
      (subItem) => path === subItem.link || path.startsWith(subItem.link + '/')
    );
  }

  return false;
};

const SidebarListSubItem = ({
  item,
  depth = 0,
  t,
  sidebarOpen,
  updateOpenSubItems,
  openSubItems,
  open,
  isPanel,
  isPlayground,
  playgroundBaseUrl,
}) => {
  const hasChildren = item.subItems && item.subItems.length > 0;
  const isLinkItem = !!item.link; // Check if this item has a link

  // Track manual toggle state and desired state
  const [manuallyToggled, setManuallyToggled] = React.useState(false);
  const [manualToggleState, setManualToggleState] = React.useState(false);

  const theme = useTheme();
  const location = useLocation();
  const path = location.pathname;
  const mdDown = useMediaQuery(theme.breakpoints.down('md'));

  // Determine if this item should be open based on the current route
  const shouldBeOpen = React.useMemo(() => {
    return isPartOfSubtree(path, item);
  }, [path, item]);

  // Update open state based on route changes
  React.useEffect(() => {
    if (!manuallyToggled && sidebarOpen) {
      // If this item should be open based on the route, open it
      if (shouldBeOpen && !open) {
        updateOpenSubItems(item.text, true);
      }
    } else if (manuallyToggled && sidebarOpen) {
      // If manually toggled, respect the manual toggle state
      if (open !== manualToggleState) {
        updateOpenSubItems(item.text, manualToggleState);
      }
    }
  }, [
    shouldBeOpen,
    open,
    item.text,
    sidebarOpen,
    manuallyToggled,
    manualToggleState,
    updateOpenSubItems,
  ]);

  // Handle toggle button click separately
  const handleToggleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!hasChildren) return;

    // Toggle the open state
    const newOpenState = !open;

    // Mark as manually toggled and save desired state
    setManuallyToggled(true);
    setManualToggleState(newOpenState);

    // Update the actual state
    updateOpenSubItems(item.text, newOpenState);

    // Never auto-reset manual toggle state
    // This allows users to keep dropdowns closed even on matching routes
  };

  // Handle item click
  const handleItemClick = (e) => {
    // For items that are not links but have children, toggle on click anywhere
    if (hasChildren && !isLinkItem && sidebarOpen) {
      e.preventDefault();
      e.stopPropagation();

      // Toggle the open state
      const newOpenState = !open;

      // Mark as manually toggled and save desired state
      setManuallyToggled(true);
      setManualToggleState(newOpenState);

      // Update the actual state
      updateOpenSubItems(item.text, newOpenState);

      return;
    }

    // For items that ARE links AND have children
    if (hasChildren && isLinkItem && sidebarOpen) {
      // For link items, we don't prevent navigation, but we do want to
      // maintain dropdown state for when user navigates back

      // If not already open, we want to remember it should be open
      if (!open) {
        setManuallyToggled(true);
        setManualToggleState(true);
        updateOpenSubItems(item.text, true);
      }

      // Don't reset manual toggle state - let the user's preference persist
    }
  };

  // Don't reset manual toggle state when sidebar closes
  // This preserves user preferences across sidebar open/close
  React.useEffect(() => {
    // We'll keep the manually toggled state and manual toggle state
    // even when the sidebar closes, so when it reopens,
    // the user's preferences are preserved
  }, [sidebarOpen]);

  // Determine the proper link
  const itemLink =
    isPanel && item.external && item.link ? item.link : item.link;

  return (
    <>
      <CustomListItem
        key={item.text}
        disablePadding
        sx={{
          display: 'block',
          // Responsive margin-bottom between md and xl
          mb: 0,
          [theme.breakpoints.only('md')]: {
            mb: 0.5,
          },
          [theme.breakpoints.only('lg')]: {
            mb: 0.25,
          },
        }}
      >
        <Tooltip
          placement="right"
          title={sidebarOpen ? '' : t(`sidebar.${item.text}`)}
        >
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              // This ensures the entire row (including toggle) gets hover styles
              '&:hover .MuiListItemButton-root': {
                backgroundColor: (theme) =>
                  theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.08)'
                    : 'rgba(0, 0, 0, 0.04)',
                '& .MuiListItemText-root, & .MuiListItemIcon-root, & svg': {
                  color: (theme) => theme.palette.primary.main,
                },
              },
            }}
          >
            <CustomListItemButton
              onClick={handleItemClick}
              sx={{
                minHeight: 40,
                display: 'flex',
                justifyContent: 'center',
                px: 2,
                py: 1,
                width: '100%',
                pr: hasChildren && sidebarOpen ? 8 : undefined, // Make space for the toggle icon
                cursor: !isLinkItem && hasChildren ? 'pointer' : undefined, // Show pointer cursor for toggleable items
                // Responsive padding between md and xl
                [theme.breakpoints.only('md')]: {
                  px: 1.5,
                  py: 0.75,
                  pr: hasChildren && sidebarOpen ? 6 : undefined,
                },
                [theme.breakpoints.only('lg')]: {
                  px: 1.75,
                  py: 0.875,
                  pr: hasChildren && sidebarOpen ? 7 : undefined,
                },
              }}
              // Always use the proper link, especially in panel mode
              LinkComponent={item.link ? (item.external ? 'a' : Link) : 'div'}
              to={item.link && !item.external ? item.link : undefined}
              href={item.link && item.external ? item.link : undefined}
              target={item.external && item.link ? item.target : undefined}
              isActive={(location.pathname === item.link).toString()}
            >
              <ListItemText
                primary={t(`sidebar.${item.text}`)}
                primaryTypographyProps={{
                  sx: {
                    fontSize: {
                      xs:
                        location.pathname === item.link
                          ? '16px !important'
                          : '12px !important',
                      md:
                        location.pathname === item.link
                          ? '13px !important'
                          : '10px !important',
                      lg:
                        location.pathname === item.link
                          ? '14px !important'
                          : '11px !important',
                      xl:
                        location.pathname === item.link
                          ? '16px !important'
                          : '12px !important',
                    },
                    fontWeight: location.pathname === item.link ? 700 : 600,
                  },
                }}
                sx={{
                  display: sidebarOpen ? 'block' : 'none',
                  color: 'secondary.neutral_60',
                  pl: depth * 4,
                  // Responsive left padding between md and xl
                  [theme.breakpoints.only('md')]: {
                    pl: depth * 3,
                  },
                  [theme.breakpoints.only('lg')]: {
                    pl: depth * 3.5,
                  },
                }}
              />
            </CustomListItemButton>

            {/* Only show separate toggle icon for items that ARE links and have children */}
            {hasChildren && isLinkItem && sidebarOpen && (
              <ToggleIconButton onClick={handleToggleClick}>
                <ListItemToggleIcon
                  className={open ? 'rotated' : ''}
                  src={
                    theme.palette.mode === 'dark'
                      ? '/static/img/icon/arrow-down-dark.svg'
                      : '/static/img/icon/arrow-down-light.svg'
                  }
                />
              </ToggleIconButton>
            )}

            {/* For non-link items with children, show the icon inline */}
            {hasChildren && !isLinkItem && sidebarOpen && (
              <Box
                sx={{
                  position: 'absolute',
                  right: 12,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none', // Let clicks pass through to the parent
                  padding: theme.spacing(4, 3),
                }}
              >
                <ListItemToggleIcon
                  className={open ? 'rotated' : ''}
                  src={
                    theme.palette.mode === 'dark'
                      ? '/static/img/icon/arrow-down-dark.svg'
                      : '/static/img/icon/arrow-down-light.svg'
                  }
                />
              </Box>
            )}
          </Box>
          <Collapse
            in={open}
            timeout={300}
            unmountOnExit
            sx={{
              willChange: 'height',
              overflow: 'hidden',
              transition: 'height 300ms cubic-bezier(0.4, 0, 0.2, 1)',
              '& .MuiCollapse-wrapper': {
                willChange: 'height',
              },
              '& .MuiCollapse-wrapperInner': {
                willChange: 'height',
              },
            }}
          >
            <List component="div" disablePadding>
              {/*subsubitems*/}
              {item?.subItems?.map((subItem) => (
                <CustomListItem
                  key={subItem.text}
                  disablePadding
                  sx={{
                    display: 'block',
                    // Responsive margin-bottom between md and xl
                    mb: 0,
                    [theme.breakpoints.only('md')]: {
                      mb: 0.5,
                    },
                    [theme.breakpoints.only('lg')]: {
                      mb: 0.25,
                    },
                  }}
                >
                  <Tooltip
                    placement="right"
                    title={sidebarOpen ? '' : t(`sidebar.${subItem.text}`)}
                  >
                    <ListItemButton
                      sx={{
                        minHeight: 48,
                        borderRadius: theme.spacing(2),
                        display: 'flex',
                        justifyContent: 'center',
                        pr: mdDown ? 6 : 6,
                        py: 2,
                        pl: 2,
                        // Responsive padding between md and xl
                        [theme.breakpoints.only('md')]: {
                          py: 1.5,
                          pl: 1.5,
                          pr: mdDown ? 4.5 : 4.5,
                        },
                        [theme.breakpoints.only('lg')]: {
                          py: 1.75,
                          pl: 1.75,
                          pr: mdDown ? 5.25 : 5.25,
                        },
                        backgroundColor:
                          location.pathname === subItem.link
                            ? theme.palette.mode === 'dark'
                              ? theme.palette.secondary.surface_22
                              : theme.palette.secondary.secondary_99
                            : 'transparent',
                        '&:hover': {
                          backgroundColor: (theme) =>
                            location.pathname === subItem.link
                              ? theme.palette.mode === 'dark'
                                ? theme.palette.secondary.surface_22
                                : theme.palette.secondary.secondary_99
                              : theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.08)'
                                : 'rgba(0, 0, 0, 0.04)',
                          '& .MuiListItemText-root': {
                            color: (theme) => theme.palette.primary.main,
                          },
                        },
                      }}
                      // Make sure subsubitems always navigate to their proper routes
                      LinkComponent={subItem.external ? 'a' : Link}
                      to={subItem.external ? null : subItem.link}
                      href={subItem.external ? subItem.link : null}
                      target={subItem.external ? subItem.target : null}
                    >
                      <ListItemText
                        primary={
                          subItem.badgeContent && sidebarOpen ? (
                            <Box
                              component="span"
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                              }}
                            >
                              <span>{t(`sidebar.${subItem.text}`)}</span>
                              <Box
                                component="span"
                                sx={{
                                  border:
                                    subItem.badgeContent === 'Beta'
                                      ? '1px solid #D8B4FE'
                                      : `1px solid ${theme.palette.primary.main}`,
                                  background:
                                    subItem.badgeContent === 'Beta'
                                      ? 'rgba(243, 232, 255, 0.88)'
                                      : theme.palette.mode === 'dark'
                                        ? theme.palette.secondary.secondary_10
                                        : '#E1F2FD',
                                  borderRadius: '99px',
                                  padding: theme.spacing(0.25, 1.5, 0.25),
                                  fontSize: '10px !important',
                                  color:
                                    subItem.badgeContent === 'Beta'
                                      ? '#8B5CF6'
                                      : theme.palette.primary.main,
                                  marginLeft: theme.spacing(3),
                                  fontWeight: 500,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  height: theme.spacing(5.25),
                                }}
                              >
                                {subItem.badgeContent}
                              </Box>
                            </Box>
                          ) : (
                            t(`sidebar.${subItem.text}`)
                          )
                        }
                        primaryTypographyProps={{
                          sx: {
                            fontSize: {
                              xs: '14px !important',
                              md: '12px !important',
                              // lg: '12px !important',
                              xl: '14px !important',
                            },
                            color:
                              (
                                location.pathname === subItem.link
                              ).toString() === 'true'
                                ? theme.palette.mode === 'dark'
                                  ? '#8CCFF9'
                                  : '#0486D7'
                                : theme.palette.mode === 'dark'
                                  ? '#C6C6C6'
                                  : '#787878',
                            fontWeight:
                              (
                                location.pathname === subItem.link
                              ).toString() === 'true'
                                ? '700'
                                : '400',
                          },
                        }}
                        sx={{
                          display: sidebarOpen ? 'block' : 'none',
                          color: 'secondary.neutral_60',
                          pl: depth * -9,
                          // Responsive left padding between md and xl
                          [theme.breakpoints.only('md')]: {
                            pl: depth * -7,
                          },
                          [theme.breakpoints.only('lg')]: {
                            pl: depth * -8,
                          },
                        }}
                      />
                    </ListItemButton>
                  </Tooltip>
                </CustomListItem>
              ))}
            </List>
          </Collapse>
        </Tooltip>
      </CustomListItem>
    </>
  );
};

export default SidebarListSubItem;
